import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { IoMdArrowBack } from "react-icons/io";
import * as api from "../../api/index";

// ✅ Lagos LGAs
const lagosLGAs = [

  "Agege",
  "Ajeromi-Ifelodun",
  "Alimosho",
  "Amuwo-Odofin",
  "Apapa",
  "Badagry",
  "Epe",
  "Eti-Osa",
  "Ibeju-Lekki",
  "Ifako-Ijaiye",
  "Ikeja",
  "Ikorodu",
  "Kosofe",
  "Lagos Island",
  "Lagos Mainland",
  "Mushin",
  "Ojo",
  "Oshodi-Isolo",
  "Shomolu",
  "Surulere",
];

export default function GetRiders() {
  const navigate = useNavigate();

  // ✅ Decode token (optional)
  const token = localStorage.getItem("token");
  let userId = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      userId = decoded?._id || decoded?.id;
    } catch {
      console.warn("Invalid token, continuing as guest");
    }
  }

  // ✅ State
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lga, setLga] = useState("All Lagos");
  const [error, setError] = useState("");
  const [chatError, setChatError] = useState("");

  // ✅ Fetch Riders
  const fetchRiders = async () => {
    try {
      setLoading(true);
      const res = await api.getRidersByLocation(lga); // Pass selected LGA
      setRiders(res.data?.riders || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load riders.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Refetch on LGA change
  useEffect(() => {
    fetchRiders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lga]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 p-6">
      {/* 🔙 Back Header Section */}
      <div className="w-full mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-700 hover:text-gray-900"
        >
          <IoMdArrowBack size={24} />
          <span className="ml-1">Back</span>
        </button>
      </div>

      {/* 🏍 Header */}
      <div className="flex flex-col sm:flex-col sm:items-start items-start mb-6">
        <h1 className="text-2xl font-bold mb-1">
          Riders — {lga || "All Lagos"}
        </h1>

        {/* 🌍 LGA Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700 font-medium">
            Choose your location:
          </label>
          <select
            value={lga}
            onChange={(e) => setLga(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {lagosLGAs.map((item, idx) => (
              <option key={idx} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        {/* 🔄 Refresh Button */}
        <button
          onClick={fetchRiders}
          className="bg-blue-600 mt-3 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition"
        >
          Refresh Riders
        </button>
      </div>

      {/* ⏳ Loading / Error / Empty States */}
      {loading && <p className="text-center text-gray-500">Loading riders...</p>}
      {!loading && error && <p className="text-center text-red-500">{error}</p>}
      {!loading && !error && riders.length === 0 && (
        <p className="text-center text-gray-500">No riders found.</p>
      )}

      {/* 🏍 Riders List */}
      <div className="grid grid-cols-3 md:grid-cols-1 gap-6">
        {!loading &&
          !error &&
          riders.map((rider) => (
            <div
              key={rider._id}
              className="bg-white rounded-2xl shadow hover:shadow-lg transition p-4 flex flex-col"
            >
              <img
                src={
                  rider.profileImage
                    ? `${rider.profileImage}`
                    : "/placeholder.jpg"
                }
                alt={rider.Name}
                className="w-full h-32 object-cover rounded-xl mb-3"
              />
              <h2 className="font-semibold text-lg">{rider.Name}</h2>
              <p className="text-gray-500 text-sm">{rider.lga || "Lagos"}</p>
              <p className="text-gray-500 text-sm line-clamp-2">
                {rider.address}
              </p>

              <div className="flex justify-between mt-4 space-x-2">
                <button
                onClick={() => navigate(`/ridershop/${rider._id}`)}
                  className="flex-1 bg-orange-500 text-white py-2 rounded-xl hover:bg-orange-600 transition"
                >
                  View Profile
                </button>
              </div>

              {/* Inline Chat Error */}
              {chatError && (
                <p className="text-red-500 text-sm mt-2 text-center">
                  {chatError}
                </p>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
